import { type Editor } from '@tiptap/react'
import { LinkDialog } from '@/components/inbox/LinkDialog'
import { ImageDialog, type ImageAttrs } from '@/components/help_center/ImageDialog'
import { ArticleLinkDialog, type LinkableArticle } from '@/components/help_center/ArticleLinkDialog'
import { VideoDialog } from '@/components/help_center/VideoDialog'
import { LoomDialog } from '@/components/help_center/LoomDialog'
import type { DialogKind } from './dialogKinds'

interface EditorDialogsProps {
  editor: Editor
  open: DialogKind | null
  onClose: () => void
  articles: LinkableArticle[]
  accountSlug: string
}

// All the editor's insert/edit dialogs and the chain commands that apply them.
// Lifted out of the toolbar so the slash menu can open the same dialogs.
export function EditorDialogs({ editor, open, onClose, articles, accountSlug }: EditorDialogsProps) {
  const change = (next: boolean) => { if (!next) onClose() }

  const applyLink = (text: string, url: string) => {
    const chain = editor.chain().focus().extendMarkRange('link')
    if (!url) {
      chain.unsetLink().run()
    } else if (editor.state.selection.empty && text) {
      chain.insertContent({ type: 'text', text, marks: [ { type: 'link', attrs: { href: url } } ] }).run()
    } else {
      chain.setLink({ href: url }).run()
    }
  }

  const applyArticleLink = ({ href, title }: { href: string; title: string }) => {
    if (editor.state.selection.empty) {
      editor.chain().focus().insertContent({ type: 'text', text: title, marks: [ { type: 'link', attrs: { href } } ] }).run()
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run()
    }
  }

  const applyImage = ({ src, alt, width, originalSrc }: ImageAttrs) => {
    if (!src) return
    const attrs = { src, alt, width, originalSrc: originalSrc ?? null }
    if (editor.isActive('image')) {
      editor.chain().focus().updateAttributes('image', attrs).run()
    } else {
      editor.chain().focus().insertContent({ type: 'image', attrs }).run()
    }
  }

  const imageAttrs = editor.isActive('image')
    ? {
        src: (editor.getAttributes('image').src as string) ?? '',
        alt: (editor.getAttributes('image').alt as string) ?? '',
        width: (editor.getAttributes('image').width as number) ?? null,
        originalSrc: (editor.getAttributes('image').originalSrc as string) ?? null,
      }
    : null

  return (
    <>
      <LinkDialog
        open={open === 'link'}
        onOpenChange={change}
        initialUrl={editor.getAttributes('link').href ?? ''}
        onSubmit={applyLink}
      />
      <ImageDialog open={open === 'image'} onOpenChange={change} initial={imageAttrs} onSubmit={applyImage} />
      <ArticleLinkDialog
        open={open === 'article'}
        onOpenChange={change}
        articles={articles}
        accountSlug={accountSlug}
        onSubmit={applyArticleLink}
      />
      <VideoDialog
        open={open === 'video'}
        onOpenChange={change}
        onSubmit={url => editor.chain().focus().setYoutubeVideo({ src: url }).run()}
      />
      <LoomDialog
        open={open === 'loom'}
        onOpenChange={change}
        onSubmit={url => editor.chain().focus().setLoomVideo({ src: url }).run()}
      />
    </>
  )
}
